import { NavLink, useLocation } from "react-router-dom";
import Logo from "../common/logo";
import {
  AppBar,
  Toolbar,
  Box,
  Stack,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  Typography,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { useState } from "react";
import LoginLogoutButton from "../auth/login-logout-button";
import { useMsal } from "@azure/msal-react";
import TenantInfoCard from "../../pages/TenantInfoCard";
import { useHasPermission } from '../../hooks/useHasPermission';

const navLinks = [
  { label: 'Dashboard', to: '/dashboard', requiresPermission: null }, // Dashboard accessible to all
  { label: 'Assets', to: '/assets', requiresPermission: 'assets' },
  { label: 'Documents', to: '/document', requiresPermission: 'documents-search' },
  { label: 'Admin', to: '/admin', requiresPermission: 'accessmanagement' },
];

const Header = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  const hasOnboardingPermission = useHasPermission('onboarding', 'read');
  const hasAdminPermission = useHasPermission('accessmanagement', 'read');
  const hasAssetsAssembliesPermission = useHasPermission('assets-assemblies', 'read');
  const hasAssetsAirframesPermission = useHasPermission('assets-airframes', 'read');
  const hasAssetsBulkTemplatePermission = useHasPermission('assets-bulk-template-upload', 'read');
  const hasAssetsPermission =
    hasAssetsAssembliesPermission ||
    hasAssetsAirframesPermission ||
    hasAssetsBulkTemplatePermission;
  const hasIataDocumentPermission = useHasPermission('documents-search', 'read');
  const hasNasDocumentPermission = useHasPermission('documents-nas', 'read');
  const hasDocumentPermission = hasIataDocumentPermission || hasNasDocumentPermission;

  const handleDrawerToggle = () => {
    setDrawerOpen((prev: boolean) => !prev);
  };

  // Check if On-Boarding menu should be active
  const isOnBoardingActive = location.pathname === '/on-boarding' || location.pathname.startsWith('/on-board/');

  // Filter nav links based on permissions
  const visibleNavLinks = navLinks.filter(link => {
    if (!link.requiresPermission) return true; // Show links with no permission requirement
    if (link.requiresPermission === 'onboarding') return hasOnboardingPermission;
    if (link.requiresPermission === 'accessmanagement') return hasAdminPermission;
    if (link.requiresPermission === 'documents-search') return hasDocumentPermission;
    if (link.requiresPermission === 'assets') return hasAssetsPermission;
    return true;
  });

  const { accounts } = useMsal();
  const user = accounts && accounts.length > 0 ? accounts[0] : null;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleProfileMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleProfileClose = () => {
    setAnchorEl(null);
  };

  return (
    <AppBar
      position="static"
      sx={{
        bgcolor: "#000",
        borderBottom: "1px solid #fff",
        boxShadow: "none",
      }}
    >
      <Toolbar
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: { xs: 1, sm: 3 },
        }}
      >
        <Box
          component="a"
          href="/"
          sx={{ display: "flex", alignItems: "center", textDecoration: "none" }}
        >
          <Logo />
        </Box>
        {/* Desktop Nav */}
        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            alignItems: "center",
            gap: 2,
          }}
        >
          <Stack direction="row" spacing={2} sx={{ width: "100%" }}>
            {visibleNavLinks.map((link) => (
              <Button
                key={link.to}
                component={NavLink}
                to={link.to}
                sx={{
                  color: "hsl(19 101% 51% / 1)",
                  textTransform: "uppercase",
                  fontWeight: 600,
                    borderBottom: '2px solid transparent',
                    ...(link.label === 'On-Boarding' && isOnBoardingActive && {
                        borderBottom: '2px solid #fff',
                    }),
                  "&.active": {
                    borderBottom: "2px solid #fff",
                  },
                  "&:hover": {
                    color: "hsl(19 101% 41% / 1)",
                    borderBottom: "2px solid #fff",
                  },
                  minWidth: 0,
                  px: 1.5,
                }}
              >
                {link.label}
              </Button>
            ))}
          </Stack>
          {user && (
            <>
              <IconButton onClick={handleProfileMenu} sx={{ ml: 1 }}>
                <Avatar
                  sx={{
                    bgcolor: "hsl(19 101% 51% / 1)",
                    width: 32,
                    height: 32,
                    fontSize: 18,
                  }}
                >
                 {user.name ? user.name[0] : user.username[0]}
                
                </Avatar>
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleProfileClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                sx={{ mt: 1 }}
              >
                <MenuItem disabled>
                  <Box sx={{ fontWeight: 600, color: "#222" }}>
                    
                    <Typography variant="caption" fontSize={20} fontWeight={900} color="#000">
                     {user.name || user.username}
                  </Typography>
                  </Box>
                </MenuItem>                
                <Divider />
                <TenantInfoCard />
                <MenuItem onClick={handleProfileClose} sx={{margin:"0 25%"}} disableRipple>
                  <LoginLogoutButton />
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>
        {/* Mobile Nav */}
        <Box
          sx={{
            display: { xs: "flex", md: "none" },
            alignItems: "center",
            gap: 1,
          }}
        >
          <IconButton
            color="inherit"
            edge="end"
            aria-label="menu"
            onClick={handleDrawerToggle}
            sx={{ color: "hsl(19 101% 51% / 1)" }}
          >
            <MenuIcon />
          </IconButton>
          <Drawer
            anchor="right"
            open={drawerOpen}
            onClose={handleDrawerToggle}
            PaperProps={{
              sx: { bgcolor: "#000", color: "hsl(19 101% 51% / 1)" },
            }}
          >
            <Box
              sx={{
                width: 220,
                pt: 2,
                display: "flex",
                flexDirection: "column",
                height: "100%",
              }}
              role="presentation"
              onClick={handleDrawerToggle}
            >
              {user && (
                <Box
                  sx={{
                    px: 2,
                    py: 2,
                    borderBottom: "1px solid #333",
                    fontWeight: 600,
                    color: "#fff",
                  }}
                >
                {user.name ? user.name[0] : user.username[0]}
                <Divider />
                <TenantInfoCard />
                
                </Box>
              )}
              <List>
                {navLinks.map((link) => (
                  <ListItem key={link.to} disablePadding>
                    <ListItemButton
                      component={NavLink}
                      to={link.to}
                      sx={{
                        color: "hsl(19 101% 51% / 1)",
                        textTransform: "uppercase",
                        fontWeight: 600,
                        "&.active": {
                          borderLeft: "4px solid #fff",
                          bgcolor: "rgba(255,255,255,0.04)",
                        },
                      }}
                    >
                      <ListItemText primary={link.label} />
                    </ListItemButton>
                  </ListItem>
                ))}
                <Divider sx={{ bgcolor: "#fff", my: 1 }} />
                <ListItem disablePadding>
                  <Box sx={{ width: "100%", px: 2, py: 1 }}>
                    <LoginLogoutButton />
                  </Box>
                </ListItem>
              </List>
            </Box>
          </Drawer>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
